// Company validation utilities and constants

// Company constants
export const INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance',
  'Education',
  'Retail',
  'Manufacturing',
  'Real Estate',
  'Consulting',
  'Media',
  'Non-profit',
  'Government',
  'Other'
];

export const COMPANY_SIZES = [
  { value: 'startup', label: 'Startup (1-10)' },
  { value: 'small', label: 'Small (11-50)' },
  { value: 'medium', label: 'Medium (51-200)' },
  { value: 'large', label: 'Large (201-1000)' },
  { value: 'enterprise', label: 'Enterprise (1000+)' }
];

// File upload constraints
export const LOGO_CONSTRAINTS = {
  maxSize: 5 * 1024 * 1024, // 5MB
  allowedTypes: ['image/jpeg', 'image/png', 'image/svg+xml'],
  allowedExtensions: ['.jpg', '.jpeg', '.png', '.svg']
};

// Year constraints
export const FOUNDED_YEAR_CONSTRAINTS = {
  minYear: 1800,
  maxYear: new Date().getFullYear()
};

// Validation functions
export const validateCompanyName = (name) => {
  if (!name || !name.trim()) {
    return 'Company name is required';
  }
  if (name.trim().length < 2) {
    return 'Company name must be at least 2 characters';
  }
  return null;
};

export const validateIndustry = (industry) => {
  if (!industry) {
    return 'Industry is required';
  }
  if (!INDUSTRIES.includes(industry)) {
    return 'Please select a valid industry';
  }
  return null;
};

export const validateWebsite = (website) => {
  if (!website) {
    return null; // Website is optional
  }
  
  try {
    const url = new URL(website);
    // Only allow http and https protocols
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return 'Please enter a valid URL';
    }
    return null;
  } catch (_) {
    return 'Please enter a valid URL';
  }
};

export const validateFoundedYear = (year) => {
  if (!year) {
    return null; // Founded year is optional
  }
  
  const yearNum = parseInt(year);
  if (isNaN(yearNum) || yearNum.toString() !== year.toString()) {
    return 'Please enter a valid year';
  }
  
  if (yearNum < FOUNDED_YEAR_CONSTRAINTS.minYear || yearNum > FOUNDED_YEAR_CONSTRAINTS.maxYear) {
    return `Please enter a valid year between ${FOUNDED_YEAR_CONSTRAINTS.minYear} and ${FOUNDED_YEAR_CONSTRAINTS.maxYear}`;
  }
  
  return null;
};

export const validateLogoFile = (file) => {
  if (!file) {
    return null; // Logo is optional
  }
  
  if (file.size > LOGO_CONSTRAINTS.maxSize) {
    return 'Logo file must be smaller than 5MB';
  }
  
  if (!LOGO_CONSTRAINTS.allowedTypes.includes(file.type)) {
    return 'Logo must be a JPG, PNG, or SVG file';
  }
  
  return null;
};

export const validateCompanySize = (size) => {
  if (!size) {
    return null; // Company size is optional
  }
  
  const validSizes = COMPANY_SIZES.map(s => s.value);
  if (!validSizes.includes(size)) {
    return 'Please select a valid company size';
  }
  
  return null;
};

export const validateLocation = (location) => {
  // Location is optional and has no specific validation rules
  return null;
};

export const validateDescription = (description) => {
  // Description is optional and has no specific validation rules
  return null;
};

// Comprehensive company validation function
export const validateCompanyData = (companyData, logoFile = null) => {
  const errors = {};
  
  const nameError = validateCompanyName(companyData.name);
  if (nameError) errors.name = nameError;
  
  const industryError = validateIndustry(companyData.industry);
  if (industryError) errors.industry = industryError;
  
  const websiteError = validateWebsite(companyData.website);
  if (websiteError) errors.website = websiteError;
  
  const foundedYearError = validateFoundedYear(companyData.foundedYear);
  if (foundedYearError) errors.foundedYear = foundedYearError;
  
  const logoError = validateLogoFile(logoFile);
  if (logoError) errors.logo = logoError;
  
  const companySizeError = validateCompanySize(companyData.companySize);
  if (companySizeError) errors.companySize = companySizeError;
  
  const locationError = validateLocation(companyData.location);
  if (locationError) errors.location = locationError;
  
  const descriptionError = validateDescription(companyData.description);
  if (descriptionError) errors.description = descriptionError;
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Helper function to format company size for display
export const formatCompanySize = (sizeValue) => {
  const size = COMPANY_SIZES.find(s => s.value === sizeValue);
  return size ? size.label : sizeValue;
};

// Helper function to check if URL is valid
export const isValidUrl = (string) => {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};